import {
  ASSESSMENT_BASE_PATH,
  ASSESSMENT_STEPS,
  RATING_SCALE,
  READINESS_OPTIONS,
  businessEffectQuestion,
  peoplePlanetQuestion,
} from '../../config/assessmentConfig'
import {
  BUSINESS_ACTIVITY_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  EMIRATE_OPTIONS,
  EXPORT_MARKET_OPTIONS,
  FOOTPRINT_OPTIONS,
  LISTING_STATUS_OPTIONS,
  MAINLAND_FREEZONE_OPTIONS,
  REVENUE_BAND_OPTIONS,
  SECTOR_OPTIONS,
  TRI_OPTIONS,
} from '../../data/assessment/companyProfile'
import { carbonSourcesForSector } from '../../data/assessment/carbonSources'
import {
  EVIDENCE_QUESTIONS,
  REPORTING_QUESTIONS,
  STAKEHOLDER_QUESTIONS,
  sectorBranchQuestionsFor,
} from '../../data/assessment/questions'
import { coreTopicsForSector, extendedTopicsForSector } from '../../data/assessment/topics'
import { trackAssessmentEvent } from '../../lib/analytics/events'
import { computeAssessmentResult, isQuestionVisible } from '../../lib/assessment/engine'
import { saveAssessmentState } from '../../lib/assessment/session'
import type { AssessmentState, RatingValue, ReadinessValue, TriState } from '../../lib/assessment/types'
import { validateCompanyProfile } from '../../lib/assessment/validation'
import { clear, el, errorBox, fieldLabel, helpText, optionSelect } from './ui'

const STEP_ORDER = ASSESSMENT_STEPS.map((s) => s.id)

function stepIndex(id: string): number {
  return Math.max(0, STEP_ORDER.indexOf(id as (typeof STEP_ORDER)[number]))
}

export function mountWizard(root: HTMLElement, state: AssessmentState): void {
  trackAssessmentEvent('assessment_start')
  render(root, state)
}

function render(root: HTMLElement, state: AssessmentState): void {
  clear(root)
  const idx = stepIndex(state.currentStep)
  const step = ASSESSMENT_STEPS[idx]

  const shell = el('div', { className: 'assess-shell' })
  const progress = el('div', { className: 'assess-progress', role: 'navigation', 'aria-label': 'Assessment progress' })
  const steps = el('ol', { className: 'assess-progress__steps' })
  ASSESSMENT_STEPS.forEach((s, i) => {
    const li = el('li', {
      className:
        'assess-progress__step' +
        (i === idx ? ' is-current' : '') +
        (i < idx ? ' is-done' : '') +
        (i > idx ? ' is-upcoming' : ''),
    })
    const btn = el('button', {
      type: 'button',
      className: 'assess-progress__step-btn',
      ...(i === idx ? { 'aria-current': 'step' } : {}),
      'aria-label': `Go to step ${i + 1}: ${s.label}`,
    }) as HTMLButtonElement
    btn.append(
      el('span', { className: 'assess-progress__step-num' }, [String(i + 1)]),
      el('span', { className: 'assess-progress__step-label' }, [s.label]),
    )
    btn.addEventListener('click', () => {
      if (i === idx) return
      // Persist current answers, then jump. Validation still runs on Continue.
      saveAssessmentState(state)
      state.currentStep = STEP_ORDER[i]
      saveAssessmentState(state)
      render(root, state)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
    li.append(btn)
    steps.append(li)
  })
  const bar = el('div', { className: 'assess-progress__bar', 'aria-hidden': 'true' })
  const fill = el('div', { className: 'assess-progress__fill' })
  fill.style.width = `${((idx + 1) / ASSESSMENT_STEPS.length) * 100}%`
  bar.append(fill)
  progress.append(
    el('div', { className: 'assess-progress__meta' }, [
      el('strong', {}, [`Step ${idx + 1} of ${ASSESSMENT_STEPS.length}`]),
      el('span', {}, [`${step.label} · ~${step.estimateMin} min`]),
    ]),
    steps,
    bar,
  )

  const panel = el('div', { className: 'assess-panel', id: 'assess-step-panel' })
  const errorsHost = el('div', { id: 'assess-errors' })

  shell.append(progress, errorsHost, panel)

  if (state.currentStep === 'company') renderCompany(panel, state)
  else if (state.currentStep === 'reporting') renderQuestionList(panel, state, REPORTING_QUESTIONS)
  else if (state.currentStep === 'materiality') renderMateriality(panel, state)
  else if (state.currentStep === 'carbon') renderCarbon(panel, state)
  else if (state.currentStep === 'evidence') renderReadinessList(panel, state, EVIDENCE_QUESTIONS)
  else if (state.currentStep === 'stakeholders') renderQuestionList(panel, state, STAKEHOLDER_QUESTIONS)

  const nav = el('div', { className: 'assess-nav' })
  if (idx > 0) {
    const back = el('button', { type: 'button', className: 'btn btn--secondary' }, ['Back']) as HTMLButtonElement
    back.addEventListener('click', () => {
      state.currentStep = STEP_ORDER[idx - 1]
      saveAssessmentState(state)
      render(root, state)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
    nav.append(back)
  }
  const next = el(
    'button',
    { type: 'button', className: 'btn btn--primary' },
    [idx === STEP_ORDER.length - 1 ? 'See results' : 'Continue'],
  ) as HTMLButtonElement
  next.addEventListener('click', () => {
    const errors = collectStep(state)
    const host = root.querySelector('#assess-errors')
    if (host) {
      clear(host as HTMLElement)
      if (errors.length) {
        host.append(errorBox(errors))
        host.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        return
      }
    }
    trackAssessmentEvent('assessment_step_complete', { step: state.currentStep })
    if (idx === STEP_ORDER.length - 1) {
      state.result = computeAssessmentResult(state)
      state.completed = true
      state.completedAt = new Date().toISOString()
      saveAssessmentState(state)
      trackAssessmentEvent('assessment_complete')
      window.location.href = `${ASSESSMENT_BASE_PATH}/results`
      return
    }
    state.currentStep = STEP_ORDER[idx + 1]
    saveAssessmentState(state)
    render(root, state)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
  nav.append(next)
  shell.append(nav)
  root.append(shell)
}

function collectStep(state: AssessmentState): string[] {
  if (state.currentStep === 'company') return validateCompanyProfile(state.companyProfile)
  if (state.currentStep === 'reporting') {
    return REPORTING_QUESTIONS.filter(
      (q) =>
        q.required &&
        isQuestionVisible(q.id, state, q.showWhen) &&
        !state.responses[q.id],
    ).map((q) => `Please answer: ${q.label}`)
  }
  if (state.currentStep === 'materiality') {
    const topics = coreTopicsForSector(state.companyProfile.sector || 'other')
    const missing = topics.filter((t) => {
      const r = state.materiality[t.id]
      return !r || r.financial == null || r.impact == null
    })
    if (missing.length) {
      return [
        `Please answer both questions (business effect, and people/environment effect) for: ${missing
          .map((t) => t.name)
          .slice(0, 4)
          .join(', ')}${missing.length > 4 ? '…' : ''}`,
      ]
    }
    return []
  }
  if (state.currentStep === 'carbon') {
    const sectorQs = sectorBranchQuestionsFor(state.companyProfile.sector).filter((q) => q.required)
    const missingSector = sectorQs.filter((q) => !state.responses[q.id])
    if (missingSector.length) {
      return [`Please answer sector-specific questions (${missingSector.length} remaining).`]
    }
    const sources = carbonSourcesForSector(state.companyProfile.sector)
    const missing = sources.filter((s) => !state.carbon[s.id]?.exists)
    if (missing.length) {
      return [`Indicate whether each source exists (including Not sure). ${missing.length} remaining.`]
    }
    return []
  }
  if (state.currentStep === 'evidence') {
    return EVIDENCE_QUESTIONS.filter((q) => !state.responses[q.id]).map((q) => `Please answer: ${q.label}`)
  }
  if (state.currentStep === 'stakeholders') {
    return STAKEHOLDER_QUESTIONS.filter(
      (q) => q.required && isQuestionVisible(q.id, state, q.showWhen) && !state.responses[q.id],
    ).map((q) => `Please answer: ${q.label}`)
  }
  return []
}

function renderCompany(panel: HTMLElement, state: AssessmentState): void {
  const p = state.companyProfile
  panel.append(
    el('h2', { className: 'assess-step-title' }, ['Company profile']),
    helpText('Required fields identify sector context. Optional fields improve personalization. We do not infer legal obligations from size or revenue alone.'),
  )

  const nameId = 'companyName'
  panel.append(fieldLabel('Company name (optional)', false, nameId))
  const nameInput = el('input', {
    id: nameId,
    className: 'assess-input',
    type: 'text',
    value: p.companyName || '',
    autocomplete: 'organization',
  }) as HTMLInputElement
  nameInput.addEventListener('input', () => {
    p.companyName = nameInput.value
    saveAssessmentState(state)
  })
  panel.append(nameInput)

  const sector = optionSelect('sector', SECTOR_OPTIONS, p.sector, true)
  sector.addEventListener('change', () => {
    p.sector = sector.value as typeof p.sector
    saveAssessmentState(state)
  })

  const emirate = optionSelect('emirate', EMIRATE_OPTIONS, p.emirate, true)
  emirate.addEventListener('change', () => {
    p.emirate = emirate.value as typeof p.emirate
    saveAssessmentState(state)
  })

  const size = optionSelect('companySize', COMPANY_SIZE_OPTIONS, p.companySize, true)
  size.addEventListener('change', () => {
    p.companySize = size.value as typeof p.companySize
    saveAssessmentState(state)
  })

  const footprint = optionSelect('operatingFootprint', FOOTPRINT_OPTIONS, p.operatingFootprint, true)
  footprint.addEventListener('change', () => {
    p.operatingFootprint = footprint.value as typeof p.operatingFootprint
    saveAssessmentState(state)
  })

  const listing = optionSelect('listingStatus', LISTING_STATUS_OPTIONS, p.listingStatus, true)
  listing.addEventListener('change', () => {
    p.listingStatus = listing.value as typeof p.listingStatus
    saveAssessmentState(state)
  })

  for (const [id, label, control] of [
    ['sector', 'Company sector', sector],
    ['emirate', 'Emirate of operations', emirate],
    ['companySize', 'Company size', size],
    ['operatingFootprint', 'Operating footprint', footprint],
    ['listingStatus', 'Listing status', listing],
  ] as const) {
    panel.append(fieldLabel(label, true, id), control)
  }

  panel.append(fieldLabel('Business activities (select all that apply)', true))
  const activities = el('div', { className: 'assess-option-list assess-option-list--grid' })
  for (const opt of BUSINESS_ACTIVITY_OPTIONS) {
    const id = `act_${opt.value}`
    const checked = p.businessActivities.includes(opt.value)
    const wrap = el('label', {
      className: 'assess-option assess-option--check' + (checked ? ' is-selected' : ''),
      for: id,
    })
    const input = el('input', {
      type: 'checkbox',
      className: 'assess-option__input',
      id,
      value: opt.value,
      ...(checked ? { checked: true } : {}),
    }) as HTMLInputElement
    input.addEventListener('change', () => {
      if (input.checked) p.businessActivities = [...new Set([...p.businessActivities, opt.value])]
      else p.businessActivities = p.businessActivities.filter((v) => v !== opt.value)
      wrap.classList.toggle('is-selected', input.checked)
      saveAssessmentState(state)
    })
    wrap.append(
      input,
      el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }),
      el('span', { className: 'assess-option__text' }, [opt.label]),
    )
    activities.append(wrap)
  }
  panel.append(activities)

  panel.append(el('h3', { className: 'assess-subtitle' }, ['Optional details']))

  const facilities = el('input', {
    id: 'facilityCount',
    className: 'assess-input',
    type: 'text',
    value: p.facilityCount || '',
    placeholder: 'e.g. 3',
  }) as HTMLInputElement
  facilities.addEventListener('input', () => {
    p.facilityCount = facilities.value
    saveAssessmentState(state)
  })
  panel.append(fieldLabel('Number of facilities', false, 'facilityCount'), facilities)

  const revenue = optionSelect('revenueBand', REVENUE_BAND_OPTIONS, p.revenueBand || '')
  revenue.addEventListener('change', () => {
    p.revenueBand = revenue.value
    saveAssessmentState(state)
  })
  panel.append(fieldLabel('Annual revenue band', false, 'revenueBand'), revenue)

  const mz = optionSelect('mainlandOrFreezone', MAINLAND_FREEZONE_OPTIONS, p.mainlandOrFreezone || '')
  mz.addEventListener('change', () => {
    p.mainlandOrFreezone = mz.value
    saveAssessmentState(state)
  })
  panel.append(fieldLabel('Mainland or free-zone establishment', false, 'mainlandOrFreezone'), mz)

  panel.append(fieldLabel('Export markets (optional)'))
  const exportsBox = el('div', { className: 'assess-option-list assess-option-list--grid' })
  for (const opt of EXPORT_MARKET_OPTIONS) {
    const id = `exp_${opt.value}`
    const checked = (p.exportMarkets || []).includes(opt.value)
    const wrap = el('label', {
      className: 'assess-option assess-option--check' + (checked ? ' is-selected' : ''),
      for: id,
    })
    const input = el('input', {
      type: 'checkbox',
      className: 'assess-option__input',
      id,
      value: opt.value,
      ...(checked ? { checked: true } : {}),
    }) as HTMLInputElement
    input.addEventListener('change', () => {
      const current = new Set(p.exportMarkets || [])
      if (input.checked) current.add(opt.value)
      else current.delete(opt.value)
      p.exportMarkets = [...current]
      wrap.classList.toggle('is-selected', input.checked)
      saveAssessmentState(state)
    })
    wrap.append(
      input,
      el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }),
      el('span', { className: 'assess-option__text' }, [opt.label]),
    )
    exportsBox.append(wrap)
  }
  panel.append(exportsBox)

  const obl = optionSelect('hasReportingObligations', TRI_OPTIONS, p.hasReportingObligations || '')
  obl.addEventListener('change', () => {
    p.hasReportingObligations = (obl.value || undefined) as TriState | undefined
    saveAssessmentState(state)
  })
  panel.append(
    fieldLabel('Do you believe the company has sustainability reporting obligations or commitments?', false, 'hasReportingObligations'),
    helpText('This captures your declared expectation — not a verified legal finding.'),
    obl,
  )

  const cust = optionSelect('customersRequestEsg', TRI_OPTIONS, p.customersRequestEsg || '')
  cust.addEventListener('change', () => {
    p.customersRequestEsg = (cust.value || undefined) as TriState | undefined
    saveAssessmentState(state)
  })
  panel.append(fieldLabel('Do customers or lenders request ESG information?', false, 'customersRequestEsg'), cust)
}

function renderQuestionList(
  panel: HTMLElement,
  state: AssessmentState,
  questions: typeof REPORTING_QUESTIONS,
): void {
  panel.append(
    el('h2', { className: 'assess-step-title' }, [
      questions[0]?.section === 'reporting'
        ? 'Reporting context and business exposure'
        : 'Stakeholder expectations',
    ]),
    helpText(
      questions[0]?.section === 'reporting'
        ? 'Answers guide which readiness themes matter next. Distinguish confirmed facts from expectations. Not all UAE companies share the same obligations.'
        : 'Connect stakeholder demand to carbon data readiness and near-term actions.',
    ),
  )

  for (const q of questions) {
    if (!isQuestionVisible(q.id, state, q.showWhen)) continue
    panel.append(renderSingleQuestion(q, state, () => {
      // Re-render when answers change so conditional questions appear.
      if (q.section === 'stakeholders') {
        const root = document.getElementById('assessment-root')
        if (root) render(root, state)
      }
    }))
  }
}

function renderSingleQuestion(
  q: (typeof REPORTING_QUESTIONS)[number],
  state: AssessmentState,
  onChange?: () => void,
): HTMLElement {
  const wrap = el('fieldset', { className: 'assess-fieldset' })
  wrap.append(el('legend', { className: 'assess-fieldset__legend' }, [q.label + (q.required ? ' *' : '')]))
  if (q.helpText) wrap.append(helpText(q.helpText))
  const list = el('div', { className: 'assess-option-list', role: 'radiogroup', 'aria-label': q.label })
  for (const opt of q.options || []) {
    const id = `${q.id}_${opt.value}`
    const selected = state.responses[q.id] === opt.value
    const label = el('label', {
      className: 'assess-option' + (selected ? ' is-selected' : ''),
      for: id,
    })
    const input = el('input', {
      type: 'radio',
      className: 'assess-option__input',
      name: q.id,
      id,
      value: opt.value,
      ...(selected ? { checked: true } : {}),
    }) as HTMLInputElement
    input.addEventListener('change', () => {
      state.responses[q.id] = opt.value
      saveAssessmentState(state)
      onChange?.()
      // Update selected styles without full remount when possible
      list.querySelectorAll('.assess-option').forEach((node) => node.classList.remove('is-selected'))
      label.classList.add('is-selected')
    })
    label.append(
      input,
      el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }),
      el('span', { className: 'assess-option__text' }, [opt.label]),
    )
    list.append(label)
  }
  wrap.append(list)
  return wrap
}

function renderReadinessList(
  panel: HTMLElement,
  state: AssessmentState,
  questions: typeof EVIDENCE_QUESTIONS,
): void {
  panel.append(
    el('h2', { className: 'assess-step-title' }, ['Evidence, data quality and governance']),
    helpText(
      'These answers describe self-reported process maturity. They are not verified evidence quality. Use “Not sure” or “Not applicable” when needed — neither is converted to zero.',
    ),
  )

  for (const q of questions) {
    const wrap = el('div', { className: 'assess-field' })
    wrap.append(fieldLabel(q.label, true, q.id))
    const select = el('select', { id: q.id, className: 'assess-input', name: q.id }) as HTMLSelectElement
    select.append(el('option', { value: '' }, ['Select…']))
    for (const opt of READINESS_OPTIONS) {
      const o = el('option', { value: opt.value }, [opt.label]) as HTMLOptionElement
      if (state.responses[q.id] === opt.value) o.selected = true
      select.append(o)
    }
    select.addEventListener('change', () => {
      state.responses[q.id] = select.value
      saveAssessmentState(state)
    })
    wrap.append(select)
    panel.append(wrap)
  }
}

function renderMateriality(panel: HTMLElement, state: AssessmentState): void {
  const core = coreTopicsForSector(state.companyProfile.sector || 'other')
  const extended = extendedTopicsForSector(state.companyProfile.sector || 'other')
  panel.append(
    el('h2', { className: 'assess-step-title' }, ['What matters most for your business?']),
    helpText(
      'For each topic, answer two simple questions: how it could affect the business, and how it could affect people or the environment. Use your best judgment — “Not sure” is fine. Required topics are tailored to your sector; extra topics are optional.',
    ),
    el('div', { className: 'assess-scale-legend' }, [
      el('strong', {}, ['How to rate: ']),
      Object.entries(RATING_SCALE)
        .map(([n, meta]) => `${n} = ${meta.label}`)
        .join(' · '),
      ' · Not sure · Not relevant to us',
    ]),
    el('h3', { className: 'assess-subtitle' }, ['Priority topics for your sector (required)']),
  )

  for (const topic of core) panel.append(renderTopicCard(topic, state, true))

  const toggleWrap = el('div', { className: 'assess-option-list' })
  const toggleId = 'showExtendedTopics'
  const toggle = el('input', {
    type: 'checkbox',
    className: 'assess-option__input',
    id: toggleId,
    ...(state.showExtendedTopics ? { checked: true } : {}),
  }) as HTMLInputElement
  const toggleLabel = el('label', {
    className: 'assess-option assess-option--check' + (state.showExtendedTopics ? ' is-selected' : ''),
    for: toggleId,
  })
  toggle.addEventListener('change', () => {
    state.showExtendedTopics = toggle.checked
    saveAssessmentState(state)
    const root = document.getElementById('assessment-root')
    if (root) render(root, state)
  })
  toggleLabel.append(
    toggle,
    el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }),
    el('span', { className: 'assess-option__text' }, [
      `Show ${extended.length} more topics that may also matter (optional)`,
    ]),
  )
  toggleWrap.append(toggleLabel)
  panel.append(toggleWrap)

  if (state.showExtendedTopics) {
    panel.append(el('h3', { className: 'assess-subtitle' }, ['More topics (optional)']))
    for (const topic of extended) panel.append(renderTopicCard(topic, state, false))
  }

  panel.append(el('h3', { className: 'assess-subtitle' }, ['Add your own topic (optional)']))
  panel.append(
    helpText(
      'If something important is missing from the list, name it and answer the same two questions.',
    ),
  )

  if (!state.additionalTopics) state.additionalTopics = []
  const extrasHost = el('div', { id: 'additional-topics-host' })
  const redrawExtras = () => {
    clear(extrasHost)
    for (let i = 0; i < state.additionalTopics!.length; i++) {
      extrasHost.append(renderAdditionalTopic(state, i))
    }
  }
  redrawExtras()
  panel.append(extrasHost)

  const addBtn = el('button', { type: 'button', className: 'btn btn--secondary' }, [
    'Add topic',
  ]) as HTMLButtonElement
  addBtn.addEventListener('click', () => {
    state.additionalTopics = [
      ...(state.additionalTopics || []),
      {
        id: `custom_${Date.now()}`,
        name: '',
        financial: undefined as unknown as RatingValue,
        impact: undefined as unknown as RatingValue,
      },
    ]
    saveAssessmentState(state)
    redrawExtras()
  })
  panel.append(addBtn)
}

function renderAdditionalTopic(state: AssessmentState, index: number): HTMLElement {
  const item = state.additionalTopics![index]
  const card = el('div', { className: 'assess-topic-card' })
  const name = el('input', {
    className: 'assess-input',
    type: 'text',
    value: item.name,
    placeholder: 'Topic name',
  }) as HTMLInputElement
  name.addEventListener('input', () => {
    item.name = name.value
    saveAssessmentState(state)
  })
  const fin = ratingSelect(`custom_${index}_fin`, item.financial)
  fin.addEventListener('change', () => {
    item.financial = fin.value as RatingValue
    saveAssessmentState(state)
  })
  const imp = ratingSelect(`custom_${index}_imp`, item.impact)
  imp.addEventListener('change', () => {
    item.impact = imp.value as RatingValue
    saveAssessmentState(state)
  })
  const remove = el('button', { type: 'button', className: 'btn btn--secondary btn--sm' }, [
    'Remove',
  ]) as HTMLButtonElement
  remove.addEventListener('click', () => {
    state.additionalTopics = (state.additionalTopics || []).filter((_, i) => i !== index)
    saveAssessmentState(state)
    const root = document.getElementById('assessment-root')
    if (root) render(root, state)
  })
  const topicLabel = item.name.trim() || 'this topic'
  card.append(
    fieldLabel('What topic do you want to add?'),
    name,
    fieldLabel(businessEffectQuestion(topicLabel), false, `custom_${index}_fin`),
    helpText('Think about money, customers, costs, financing, or day-to-day operations.'),
    fin,
    fieldLabel(peoplePlanetQuestion(topicLabel), false, `custom_${index}_imp`),
    helpText('Think about workers, customers, communities, or the environment.'),
    remove,
  )
  return card
}

function renderTopicCard(
  topic: ReturnType<typeof coreTopicsForSector>[number],
  state: AssessmentState,
  required: boolean,
): HTMLElement {
  const card = el('div', { className: 'assess-topic-card' })
  card.append(
    el('div', { className: 'assess-pill' }, [
      required ? 'Required for your sector' : 'Optional',
      ` · ${topic.pillar === 'environmental' ? 'Environment' : topic.pillar === 'social' ? 'People' : 'How the company is run'}`,
    ]),
    el('h3', {}, [topic.name]),
    el('p', { className: 'assess-help' }, [topic.description]),
    el('p', { className: 'assess-help' }, [`In the UAE context: ${topic.uaeConsiderations}`]),
  )

  const current = state.materiality[topic.id] || {
    financial: undefined as unknown as RatingValue,
    impact: undefined as unknown as RatingValue,
  }

  const fin = ratingSelect(`${topic.id}_fin`, current.financial)
  fin.addEventListener('change', () => {
    if (!fin.value) {
      delete state.materiality[topic.id]
      saveAssessmentState(state)
      return
    }
    const existing = state.materiality[topic.id]
    state.materiality[topic.id] = {
      financial: fin.value as RatingValue,
      impact: existing?.impact as RatingValue,
      rationale: existing?.rationale,
    }
    saveAssessmentState(state)
  })

  const imp = ratingSelect(`${topic.id}_imp`, current.impact)
  imp.addEventListener('change', () => {
    if (!imp.value) {
      saveAssessmentState(state)
      return
    }
    const existing = state.materiality[topic.id]
    state.materiality[topic.id] = {
      financial: existing?.financial as RatingValue,
      impact: imp.value as RatingValue,
      rationale: existing?.rationale,
    }
    saveAssessmentState(state)
  })

  const bizQ = businessEffectQuestion(topic.name, topic.businessEffectQuestion)
  const planetQ = peoplePlanetQuestion(topic.name, topic.peoplePlanetQuestion)

  card.append(
    el('div', { className: 'assess-topic-q' }, [
      fieldLabel(bizQ, required, `${topic.id}_fin`),
      helpText(`Examples: ${topic.financialRisksOpportunities}`),
      fin,
    ]),
    el('div', { className: 'assess-topic-q' }, [
      fieldLabel(planetQ, required, `${topic.id}_imp`),
      helpText(`Examples: ${topic.potentialImpacts}`),
      imp,
    ]),
  )

  const rationaleId = `${topic.id}_why`
  const rationale = el('textarea', {
    id: rationaleId,
    className: 'assess-input assess-textarea',
    rows: '2',
    placeholder: 'Optional: why did you rate this as major or critical?',
  }) as HTMLTextAreaElement
  rationale.value = current.rationale || ''
  rationale.addEventListener('input', () => {
    const existing = state.materiality[topic.id]
    if (!existing) return
    existing.rationale = rationale.value
    saveAssessmentState(state)
  })
  card.append(fieldLabel('Anything else we should know? (optional)', false, rationaleId), rationale)
  return card
}

function ratingSelect(id: string, value: RatingValue | undefined): HTMLSelectElement {
  const select = el('select', { id, className: 'assess-input', name: id }) as HTMLSelectElement
  select.append(el('option', { value: '' }, ['Select…']))
  for (const n of [1, 2, 3, 4, 5] as const) {
    const o = el('option', { value: String(n) }, [`${n} — ${RATING_SCALE[n].label}`]) as HTMLOptionElement
    if (value === n) o.selected = true
    select.append(o)
  }
  for (const special of [
    { value: 'not_sure', label: 'Not sure' },
    { value: 'not_applicable', label: 'Not relevant to us' },
  ]) {
    const o = el('option', { value: special.value }, [special.label]) as HTMLOptionElement
    if (value === special.value) o.selected = true
    select.append(o)
  }
  return select
}

function renderCarbon(panel: HTMLElement, state: AssessmentState): void {
  const sources = carbonSourcesForSector(state.companyProfile.sector)
  const sectorQs = sectorBranchQuestionsFor(state.companyProfile.sector)
  panel.append(
    el('h2', { className: 'assess-step-title' }, ['Carbon inventory readiness']),
    helpText(
      'This section assesses whether activity data and processes exist to compile a reliable GHG inventory. It does not calculate emissions. Missing data is not treated as zero emissions. Categories shown are sector-tailored and not exhaustive.',
    ),
  )

  if (sectorQs.length) {
    panel.append(
      el('h3', { className: 'assess-subtitle' }, ['Sector-specific data themes']),
      helpText('These questions tailor Scope 3 and operational readiness checks to your sector.'),
    )
    for (const q of sectorQs) panel.append(renderSingleQuestion(q, state))
  }

  for (const scope of ['1', '2', '3'] as const) {
    const scoped = sources.filter((s) => s.scope === scope)
    if (!scoped.length) continue
    panel.append(el('h3', { className: 'assess-subtitle' }, [`Scope ${scope}`]))

    for (const source of scoped) {
      const entry = state.carbon[source.id] || { sourceId: source.id }
      state.carbon[source.id] = entry
      const card = el('div', { className: 'assess-topic-card' })
      card.append(el('h4', {}, [source.label]))
      if (source.helpText) card.append(helpText(source.helpText))

      const exists = optionSelect(`${source.id}_exists`, TRI_OPTIONS, entry.exists || '', true)
      exists.addEventListener('change', () => {
        entry.exists = exists.value as TriState
        saveAssessmentState(state)
        renderCarbonDetails(details, entry, state, source.id)
      })
      card.append(fieldLabel('Does this source exist in your operations?', true), exists)

      const details = el('div', { className: 'assess-carbon-details' })
      renderCarbonDetails(details, entry, state, source.id)
      card.append(details)
      panel.append(card)
    }
  }
}

function renderCarbonDetails(
  host: HTMLElement,
  entry: AssessmentState['carbon'][string],
  state: AssessmentState,
  sourceId: string,
): void {
  clear(host)
  if (entry.exists !== 'yes') return

  const dataCollected = optionSelect(`${sourceId}_data`, TRI_OPTIONS, entry.dataCollected || '')
  dataCollected.addEventListener('change', () => {
    entry.dataCollected = dataCollected.value as TriState
    saveAssessmentState(state)
  })

  const frequency = optionSelect(
    `${sourceId}_freq`,
    [
      { value: 'monthly', label: 'Monthly' },
      { value: 'quarterly', label: 'Quarterly' },
      { value: 'annually', label: 'Annually' },
      { value: 'irregular', label: 'Irregular' },
      { value: 'not_sure', label: 'Not sure' },
    ],
    entry.frequency || '',
  )
  frequency.addEventListener('change', () => {
    entry.frequency = frequency.value
    saveAssessmentState(state)
  })

  const coverage = optionSelect(
    `${sourceId}_cov`,
    [
      { value: 'all', label: 'All relevant facilities' },
      { value: 'most', label: 'Most facilities' },
      { value: 'some', label: 'Some facilities' },
      { value: 'none', label: 'None yet' },
      { value: 'not_sure', label: 'Not sure' },
    ],
    entry.coverage || '',
  )
  coverage.addEventListener('change', () => {
    entry.coverage = coverage.value
    saveAssessmentState(state)
  })

  const evidence = optionSelect(`${sourceId}_ev`, TRI_OPTIONS, entry.evidenceAvailable || '')
  evidence.addEventListener('change', () => {
    entry.evidenceAvailable = evidence.value as TriState
    saveAssessmentState(state)
  })

  const owner = optionSelect(`${sourceId}_own`, TRI_OPTIONS, entry.ownerAssigned || '')
  owner.addEventListener('change', () => {
    entry.ownerAssigned = owner.value as TriState
    saveAssessmentState(state)
  })

  const method = optionSelect(`${sourceId}_method`, TRI_OPTIONS, entry.methodDocumented || '')
  method.addEventListener('change', () => {
    entry.methodDocumented = method.value as TriState
    saveAssessmentState(state)
  })

  const readiness = el('select', { id: `${sourceId}_ready`, className: 'assess-input' }) as HTMLSelectElement
  readiness.append(el('option', { value: '' }, ['Select…']))
  for (const opt of READINESS_OPTIONS) {
    const o = el('option', { value: opt.value }, [opt.label]) as HTMLOptionElement
    if (entry.readiness === opt.value) o.selected = true
    readiness.append(o)
  }
  readiness.addEventListener('change', () => {
    entry.readiness = readiness.value as ReadinessValue
    saveAssessmentState(state)
  })

  host.append(
    fieldLabel('Is activity data collected?'),
    dataCollected,
    fieldLabel('Collection frequency'),
    frequency,
    fieldLabel('Facility coverage'),
    coverage,
    fieldLabel('Are source documents available?'),
    evidence,
    fieldLabel('Is an owner assigned?'),
    owner,
    fieldLabel('Is the calculation method documented?'),
    method,
    fieldLabel('Overall process readiness for this source'),
    readiness,
  )
}
